import { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import ErrorDisplay from "../components/ErrorDisplay";
import AudioPlayer from "../components/AudioPlayer";
import VideoPlayer from "../components/video-player";
import type {
  Attachment,
  NfcApiResponse,
  NfcContent,
  RawNfcResponseData,
} from "../types";
import ImagePreviewer from "../components/ImagePreviewer";
import ImageSwiper from "../components/ImageSwiper";
import { cn } from "../utils/cn";
import topCornerIcon from "../assets/icon-corner-top.svg";
import bottomCornerIcon from "../assets/icon-corner-bottom.svg"

const isBrowser = typeof window !== "undefined";

type ResolvedNfcContent = NfcContent & {
  text: string;
  attachments: Attachment[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isNfcContent(value: unknown): value is NfcContent {
  if (!isRecord(value)) return false;
  return typeof value.type === "string";
}

function sanitizeAttachments(value: unknown): Attachment[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is Attachment => {
    if (!isRecord(item)) return false;
    return (
      typeof item.name === "string" &&
      typeof item.url === "string" &&
      typeof item.contentType === "string"
    );
  });
}

function resolveNfcContent(payload: RawNfcResponseData): ResolvedNfcContent | null {
  if (!payload) return null;

  if (isNfcContent(payload)) {
    return {
      ...payload,
      text: typeof payload.text === "string" ? payload.text : "",
      attachments: sanitizeAttachments(payload.attachments ?? []),
    };
  }

  if (isRecord(payload) && "data" in payload) {
    const nested =
      (payload as { data?: RawNfcResponseData | null }).data ?? null;
    return resolveNfcContent(nested);
  }

  return null;
}

function useResolvedId() {
  const params = useParams<{ id?: string }>();
  const location = useLocation();

  return useMemo(() => {
    if (params.id) return params.id;
    if (!isBrowser) return "";
    const query = new URLSearchParams(location.search);
    return query.get("id") || "";
  }, [location.search, params.id]);
}

export default function ExplorePage() {
  const id = useResolvedId();
  const [status, setStatus] = useState<"idle" | "loading" | "ready" | "error">(
    "idle"
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [content, setContent] = useState<ResolvedNfcContent | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  // 横屏检测状态（宽高比 >= 1.3 视为横屏）
  const [isLandscape, setIsLandscape] = useState<boolean>(
    isBrowser ? window.innerWidth / window.innerHeight >= 1.3 : false
  );

  // 监听屏幕方向变化和窗口大小变化
  useEffect(() => {
    if (!isBrowser) return;

    const handleResize = () => {
      setIsLandscape(window.innerWidth / window.innerHeight >= 1.3);
    };

    const handleOrientationChange = () => {
      // 延迟一点时间以确保屏幕尺寸已经更新
      setTimeout(handleResize, 100);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setStatus("error");
      setErrorMessage("NFC ID is required");
      return;
    }

    const controller = new AbortController();

    async function load() {
      setStatus("loading");
      setErrorMessage("");
      try {
        const response = await fetch(`/api/nfc/content?id=${id}`, {
          headers: {
            "Content-Type": "application/json",
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch NFC content: ${response.status} ${response.statusText}`
          );
        }

        const payload: NfcApiResponse = await response.json();
        if (typeof payload?.code === "number" && payload.code !== 0) {
          throw new Error(
            payload?.message || `Service returned error (code: ${payload.code})`
          );
        }

        const resolvedContent = resolveNfcContent(payload?.data ?? null);

        if (!resolvedContent) {
          throw new Error(payload?.message || "NFC content not found");
        }
        if (resolvedContent.type !== "custom") {
          throw new Error(`Unsupported NFC type: ${resolvedContent.type}`);
        }
        setContent(resolvedContent);
        setStatus("ready");
      } catch (error) {
        if (controller.signal.aborted) return;
        setStatus("error");
        setContent(null);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "An unknown error occurred while fetching NFC content"
        );
      }
    }

    load();
    return () => controller.abort();
  }, [id]);

  if (!id) {
    return (
      <ErrorDisplay
        message="Missing NFC ID"
        description="Please access this page by scanning the NFC tag or using a link with the id parameter."
      />
    );
  }

  if (status === "error") {
    return (
      <ErrorDisplay
        message={
          errorMessage || "An unknown error occurred while fetching NFC content"
        }
        description="We are unable to load the content at this time. Please try again later or refresh the page."
        onRetry={isBrowser ? () => window.location.reload() : undefined}
      />
    );
  }

  if (status === "loading" || !content) {
    return (
      <div
        className="min-h-screen text-[#333333] flex items-center justify-center"
        style={{
          backgroundImage: "url('/images/bg.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <p className="text-base">Loading...</p>
      </div>
    );
  }

  const { text, attachments, createdAt } = content;
  const title = createdAt ? new Date(createdAt).toLocaleDateString("en-US") : "";

  const primaryAttachment: Attachment | undefined = attachments.length > 0 ? attachments[0] : undefined;
  const hasAttachments = Boolean(primaryAttachment);
  const isImage = hasAttachments && primaryAttachment!.contentType.startsWith("image/");
  const isVideo = hasAttachments && primaryAttachment!.contentType.startsWith("video/");
  const isAudio = hasAttachments && primaryAttachment!.contentType.startsWith("audio/");

  return (
    <div
      className={cn("h-dvh text-[#333333] flex flex-col", isLandscape && "overflow-auto")}
      style={{
        backgroundImage: isLandscape ? "url('/images/bg-hp.jpg')" : "url('/images/bg.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "top center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <header className={cn("py-3 text-center text-lg font-medium", isLandscape && "hidden")}>
        {title}
      </header>

      {/* 主内容区域 */}
      <main
        className={cn("z-0 flex-1",
          isLandscape
            ? "min-h-[400px] flex justify-center items-center p-0"
            : "pt-[8.53%] pb-[12.8%] block overflow-auto"
        )}
      >
        {/* 内容卡片容器 */}
        <div
          className={cn(
            "relative mx-auto bg-[#FFE60A] flex items-center justify-center text-base transition-all duration-300 ease-in-out",
            isLandscape
              ? "flex-row-reverse w-[88.44%] h-[76.53%] rounded-[4.7vw] py-[10vh] lg:py-[10.84vh]"
              : "flex-col w-[92%] max-w-3xl min-h-[500px] md:min-h-[60vh] max-h-full gap-5 rounded-3xl pt-12"
          )}
          style={{
            backgroundImage: isLandscape ? "url('/images/bg-nfc-hp.svg')" : "url('/images/bg-nfc.svg')",
            backgroundSize: isLandscape ? "40%" : "63%",
            backgroundPosition: "top right",
            backgroundRepeat: "no-repeat",
          }}
        >
          {/* 装饰性阴影背景 */}
          {isLandscape ? (
            <>
              <div className="pointer-events-none absolute top-2.5 left-1/2 -translate-x-1/2 w-[94%] h-[50%] rotate-[6deg] rounded-[4.7vw] bg-[rgba(118,106,0,0.60)] -z-10" />
              <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 w-[94%] h-[50%] rotate-[6deg] rounded-[4.7vw] bg-[rgba(118,106,0,0.60)] -z-10" />
            </>
          ) : (
            <>
              <div className="pointer-events-none absolute -top-1 left-1/2 h-56 w-[87%] -translate-x-1/2 rotate-[5deg] rounded-3xl bg-[rgba(118,106,0,0.60)] -z-10" />
              <div className="pointer-events-none absolute -bottom-4 left-1/2 h-56 w-[87%] -translate-x-1/2 rotate-[5deg] rounded-3xl bg-[rgba(118,106,0,0.60)] -z-10" />
            </>
          )}

          {/* 文本内容区域 */}
          {text && (
            <article
              className={cn(
                "overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words font-medium",
                isLandscape
                  ? "flex-1 max-h-[80%] text-[min(1.86vw,24px)] leading-normal pl-[6.6vw] pr-[5.8vw] text-[rgba(51,51,51,0.6)]"
                  : "w-full px-12 max-h-[250px] text-base text-[#333333]"
              )}
            >
              {text}
            </article>
          )}

          {/* 附件媒体内容区域 */}
          {hasAttachments && (
            <div className={cn(isLandscape ? "w-[53.7%] h-full ml-[5.9vw] relative bg-[#fff599] rounded-[4.7vw]" : "w-full")}>
              {/* 横屏模式下的装饰图标 */}
              {isLandscape && (
                <>
                  <img src={topCornerIcon} alt="" className="w-[8.8vw] h-auto absolute top-[-4.5vw] right-[-4.8vw] z-10" />
                  <img src={bottomCornerIcon} alt="" className="w-[8.8vw] h-auto absolute bottom-[-4.5vw] left-[-4.5vw] z-10" />
                </>
              )}
              {isImage && (
                <ImageSwiper carouselItems={attachments} setSelectedImage={setSelectedImage} isLandscape={isLandscape} />
              )}
              {isAudio && primaryAttachment && <AudioPlayer attachment={primaryAttachment} />}
              {isVideo && primaryAttachment && <VideoPlayer attachment={primaryAttachment} isLandscape={isLandscape} />}
            </div>
          )}
        </div>
      </main>

      {hasAttachments && isImage && selectedImage && (
        <ImagePreviewer
          selectedImage={selectedImage}
          carouselItems={attachments}
          onClose={() => setSelectedImage(null)}
          onChange={setSelectedImage}
        />
      )}
    </div>
  );
}
